'use client';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSidebarStore } from '@/hooks/use-sidebar-store';
import { cn } from '@/lib/utils';
import { SignalMethod, SocketEndpoint } from '@/signalR/enum';
import { SendCounter } from '@/signalR/type';
import { useSocketListener } from '@/signalR/use-socket-listener';
import { LucideIcon, Router } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

export function CustomSidebarBottom({
    className,
    ...props
}: {
    className?: string;
}) {
    const transSidebar = useTranslations('Sidebar');

    const isShowSidebarIconOnly = useSidebarStore(
        (state) => state.isShowSidebarIconOnly
    );

    const showTotalBox = useSidebarStore((state) => state.showTotalBox);
    const totalOnline = useSidebarStore((state) => state.totalOnline);
    const totalAccess = useSidebarStore((state) => state.totalAccess);
    const setTotalBox = useSidebarStore((state) => state.setTotalBox);

    const onSendCounter = useCallback(
        (obj: SendCounter) => {
            if (obj.totalUserAccessCount) {
                setTotalBox({
                    showTotalBox: true,
                    totalAccess: obj.totalUserAccessCount,
                    totalOnline: 0,
                });
            }
        },
        [setTotalBox]
    );

    useSocketListener({
        endPoint: SocketEndpoint.hubs,
        listener: {
            [SignalMethod.SendCounter]: onSendCounter,
        },
    });

    const isNoSignalRInfo = !totalOnline && !totalAccess;

    if (isNoSignalRInfo) {
        return null;
    }

    return (
        <div
            className={cn(
                'content-center space-y-2 bg-primary py-1 pl-1 pr-2 text-primary-foreground',
                className,
                isShowSidebarIconOnly ? 'w-16' : 'w-full'
            )}
            hidden={!showTotalBox}
            {...props}
        >
            {/* <SidebarBottomDetail
                hiddenName={isShowSidebarIconOnly}
                name={transSidebar('Sidebar_OnlineUsers')}
                value={totalOnline}
                icon={User}
            /> */}
            <SidebarBottomDetail
                hiddenName={isShowSidebarIconOnly}
                name={transSidebar('Sidebar_TotalAccesses')}
                value={totalAccess}
                icon={Router}
            />
        </div>
    );
}

const SidebarBottomDetail = ({
    hiddenName,
    value,
    name,
    icon,
    className,
}: {
    name: string;
    hiddenName: boolean;
    value?: number;
    icon: LucideIcon;
    className?: string;
}) => {
    const Component = icon;

    if (hiddenName) {
        return (
            <div
                className={cn(
                    'flex cursor-pointer items-center justify-center text-sm font-medium',
                    className
                )}
            >
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger>
                            <Component className='' size={20} />
                        </TooltipTrigger>
                        <TooltipContent align='end'>
                            {name}: {value ?? '-'}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex items-center justify-between text-sm font-medium',
                className
            )}
        >
            <div className='flex items-center whitespace-nowrap'>
                <Component className='mr-2' size={20} />
                <div>{name}:</div>
            </div>
            <div className='truncate'>{value ?? '-'}</div>
        </div>
    );
};
